/**
 * @module useReviews
 * Shared state for the admin reviews management page.
 *
 * Handles listing and filtering the full review corpus, managing cooldowns,
 * loading stats, and the reviews coverage report.
 *
 * Reviews are fetched all at once from the admin endpoint, then filtered
 * client-side via `loadReviews()`. This works because the review corpus is
 * typically small (hundreds of records) and client-side filtering is instant.
 *
 * Coverage report follows the same pattern as the sales coverage: defaults to
 * last 7 days, shows a missing-details sub-dialog for individual days.
 *
 * Platforms/locations/brands are loaded from the sales service because that's
 * the single source of truth for these lookup tables (reviews don't have their
 * own options endpoint).
 */

import { ref, watch } from "vue";
import api, { isAuthenticated } from "@/utils/api.js";
import { toLocalYMD } from "@/utils/dateLocal.js";
import { useConfirmDialog } from "@/composables/useConfirmDialog";

// Initialize confirm dialog at module scope so it's available before any
// component mounts (used by admin actions triggered from the review list)
const { confirm } = useConfirmDialog();

// ── Core data ─────────────────────────────────────────────────────────────────

const loading = ref(false);
const stats = ref({});
const reviews = ref([]);
const platforms = ref([]);
const locations = ref([]);
const brands = ref([]);
const snackbar = ref({
  show: false,
  message: "",
  color: "success",
});

const showSnack = (message, color = "success") => {
  snackbar.value = { show: true, message, color };
};

// ── Coverage report state ─────────────────────────────────────────────────────

const showReviewsCoverageDialog = ref(false);
const reviewsCoverageLoading = ref(false);
const reviewsCoverageError = ref(null);
const reviewsCoverageData = ref(null);
const reviewsCoverageStartDate = ref("");
const reviewsCoverageEndDate = ref("");
const selectedReviewDay = ref(null);
const showReviewsMissingDialog = ref(false);

const getDateNDaysAgo = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return toLocalYMD(d);
};

const currentDate = toLocalYMD();

// ── Filter state ──────────────────────────────────────────────────────────────

// Start with all filters clear so the full review pool is visible on load
const filters = ref({
  platform: null,
  location: null,
  brand: null,
  rating: null,
  shownStatus: null,
  count: 4,
  cooldown: 7,
});

const REVIEWS_FILTERS_KEY = "reviewsFilters";

// Restore filters from localStorage on init
const savedFilters = localStorage.getItem(REVIEWS_FILTERS_KEY);
if (savedFilters) {
  try {
    const parsed = JSON.parse(savedFilters);
    filters.value = { ...filters.value, ...parsed };
  } catch {}
}

// Auto-persist filters to localStorage on change
watch(filters, (val) => {
  localStorage.setItem(REVIEWS_FILTERS_KEY, JSON.stringify(val));
}, { deep: true });

// ── Clear filters ─────────────────────────────────────────────────────────────

function clearFilters() {
  filters.value = {
    platform: null,
    location: null,
    brand: null,
    rating: null,
    shownStatus: null,
    count: 4,
    cooldown: 7,
  };
  localStorage.removeItem(REVIEWS_FILTERS_KEY);
}

// ── Data loading ──────────────────────────────────────────────────────────────

/**
 * Fetch all reviews from the admin endpoint and apply the current filter values
 * client-side. Rating is compared numerically because the API may return it as
 * a string from the database.
 */
const loadReviews = async () => {
  if (!isAuthenticated()) return;
  loading.value = true;
  try {
    const res = await api.marketingApi.get("/api/marketing/reviews/admin/reviews");
    let filtered = res.data;

    if (filters.value.platform) {
      filtered = filtered.filter((r) => r.platform === filters.value.platform);
    }
    if (filters.value.location) {
      filtered = filtered.filter((r) => r.location === filters.value.location);
    }
    if (filters.value.brand) {
      filtered = filtered.filter((r) => r.brand === filters.value.brand);
    }
    if (filters.value.rating) {
      filtered = filtered.filter((r) => Number(r.rating) === Number(filters.value.rating));
    }
    if (filters.value.shownStatus === "shown") {
      filtered = filtered.filter((r) => r.last_shown != null);
    } else if (filters.value.shownStatus === "not_shown") {
      filtered = filtered.filter((r) => r.last_shown == null);
    }

    // Normalise rating to number so comparisons in the template are consistent
    reviews.value = filtered.map((r) => ({
      ...r,
      rating: Number(r.rating),
    }));
  } catch (err) {
    showSnack("Failed to load reviews", "error");
  } finally {
    loading.value = false;
  }
};

const loadStats = async () => {
  if (!isAuthenticated()) return;
  try {
    const res = await api.marketingApi.get("/api/marketing/reviews/admin/stats");
    stats.value = res.data;
  } catch (err) {
    console.error("Failed to load stats:", err);
  }
};

/** Load platform/location/brand options from the sales service (shared lookup tables). */
const loadPlatformsAndLocations = async () => {
  if (!isAuthenticated()) return;
  try {
    const platformsRes = await api.accountingApi.get("/api/accounting/sales/options");

    if (platformsRes.data.platforms?.length) {
      platforms.value = platformsRes.data.platforms;
    }
    if (platformsRes.data.locations?.length) {
      locations.value = platformsRes.data.locations;
    }
    if (platformsRes.data.brands?.length) {
      brands.value = platformsRes.data.brands;
    }
  } catch (err) {
    console.error("Failed to load platforms/locations/brands:", err.message);
  }
};

// ── Cooldown management ───────────────────────────────────────────────────────

/** Reset a review's cooldown so it becomes eligible for selection again immediately. */
const resetCooldown = async (item) => {
  try {
    const res = await api.marketingApi.post(`/api/marketing/reviews/admin/reviews/${item.id}/reset-cooldown`, {
      platform: item.platform,
      location: item.location,
    });

    showSnack(res.data.message || "Cooldown reset successfully", "success");
    await loadReviews();
  } catch (err) {
    console.error("Error resetting cooldown:", err);
    showSnack(err.response?.data?.error || "Failed to reset cooldown", "error");
  }
};

/**
 * Show cooldown status for a specific review in an alert dialog.
 * Uses a native alert for simplicity — this is an admin debug tool.
 */
const viewCooldownStatus = async (item) => {
  try {
    const res = await api.marketingApi.get(`/api/marketing/reviews/admin/reviews/${item.id}/cooldown-status`);

    alert(`
Cooldown Status for Review #${item.id}:
${res.data.cooldown_records
  .map(
    (record) => `- ${record.platform} in ${record.location}: ${record.last_shown || "Never shown"}`,
  )
  .join("\n")}
    `);
  } catch (err) {
    console.error("Error getting cooldown status:", err);
    showSnack("Failed to get cooldown status", "error");
  }
};

// ── Composable export ─────────────────────────────────────────────────────────

export function useReviews() {
  const openReviewsCoverageDialog = () => {
    reviewsCoverageStartDate.value = getDateNDaysAgo(7);
    reviewsCoverageEndDate.value = currentDate;
    reviewsCoverageData.value = null;
    reviewsCoverageError.value = null;
    showReviewsCoverageDialog.value = true;
  };

  const closeReviewsCoverageDialog = () => {
    showReviewsCoverageDialog.value = false;
  };

  const fetchReviewsCoverage = async () => {
    if (!reviewsCoverageStartDate.value || !reviewsCoverageEndDate.value) return;

    reviewsCoverageLoading.value = true;
    reviewsCoverageError.value = null;
    reviewsCoverageData.value = null;

    try {
      const res = await api.marketingApi.post("/api/marketing/reviews/admin/coverage", {
        start_date: reviewsCoverageStartDate.value,
        end_date: reviewsCoverageEndDate.value,
      });
      reviewsCoverageData.value = res.data;
    } catch (err) {
      reviewsCoverageError.value = err.response?.data?.error || "Failed to fetch coverage data";
    } finally {
      reviewsCoverageLoading.value = false;
    }
  };

  const showReviewsMissingDetails = (dayItem) => {
    selectedReviewDay.value = dayItem;
    showReviewsMissingDialog.value = true;
  };

  const closeReviewsMissingDialog = () => {
    showReviewsMissingDialog.value = false;
  };

  return {
    // Core data
    platforms,
    locations,
    brands,
    filters,
    reviews,
    stats,
    loading,
    snackbar,

    // Coverage state
    showReviewsCoverageDialog,
    reviewsCoverageLoading,
    reviewsCoverageError,
    reviewsCoverageData,
    reviewsCoverageStartDate,
    reviewsCoverageEndDate,
    selectedReviewDay,
    showReviewsMissingDialog,
    currentDate,

    // Methods
    loadStats,
    loadReviews,
    loadPlatformsAndLocations,
    viewCooldownStatus,
    resetCooldown,
    showSnack,
    clearFilters,

    // Coverage methods
    openReviewsCoverageDialog,
    closeReviewsCoverageDialog,
    fetchReviewsCoverage,
    showReviewsMissingDetails,
    closeReviewsMissingDialog,
    getDateNDaysAgo,
  };
}
